import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
    ArrowRight, ArrowUpRight, MonitorSmartphone, PenTool, Palette,
    Facebook, RefreshCw, LifeBuoy, X, Loader2, CheckCircle2, Star,
} from "lucide-react";
import SEO from "@/components/SEO";
import MockupModal from "@/components/MockupModal";
import CircuitField from "@/components/CircuitField";
import { Reveal, KineticLine, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

const iconMap = { MonitorSmartphone, PenTool, Palette, Facebook, RefreshCw, LifeBuoy };

const HERO_BG =
    "https://images.unsplash.com/photo-1688494963827-9f9dc1c51bd0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB0ZWNoJTIwb2ZmaWNlJTIwYWJzdHJhY3QlMjBkYXJrfGVufDB8fHx8MTc4Njg4NDQ2MXww&ixlib=rb-4.1.0&q=85";

const chapters = [
    {
        n: "01",
        title: "Mobile-First Builds",
        text: "Every site looks sharp on phones and desktops — because that's exactly where your customers are looking.",
    },
    {
        n: "02",
        title: "Clear Layouts",
        text: "Visitors instantly understand what you offer, why it matters, and how to reach you. No confusion, no bounce.",
    },
    {
        n: "03",
        title: "Fast Updates",
        text: "Prices, photos, wording — simple changes whenever your business moves. Your site never goes stale.",
    },
    {
        n: "04",
        title: "Brand-Focused",
        text: "Designed around your business style and colors — never a cookie-cutter template with your name pasted on it.",
    },
];

const processSteps = [
    { n: "01", title: "Share Your Vision", text: "Tell me about your business, your style, and what you need the site to do." },
    { n: "02", title: "Design & Build", text: "I design and build your site around your brand, with your input at every step." },
    { n: "03", title: "Review & Refine", text: "You review the work and request changes until it feels exactly right." },
    { n: "04", title: "Launch & Support", text: "Your site goes live — and stays fresh with updates whenever you need them." },
];

const bentoSpans = [
    "md:col-span-2 lg:col-span-7",
    "md:col-span-2 lg:col-span-5",
    "lg:col-span-4",
    "lg:col-span-4",
    "lg:col-span-4",
];

export default function Home() {
    const sc = useSiteContent();
    const { services, featuredProject, clientProjects, marqueeItems, reviews, CONTACT_EMAIL } = sc;
    const heroData = sc.hero || {};
    const aboutData = sc.about || {};
    const chaptersData = sc.manifesto && sc.manifesto.length ? sc.manifesto : chapters;
    const processStepsData = sc.processSteps && sc.processSteps.length ? sc.processSteps : processSteps;
    const defaultTestimonials = [
        { quote: "I love it!", name: "Toneia", sub: "Briars Unlimited — Website Client", tag: "", layout: "big" },
        { quote: "WOW! It looks so good!!", name: "Toneia's sister-in-law", sub: "On seeing the Briars Unlimited site", tag: "", layout: "panel" },
        { quote: "It looks so much better! So excited!", name: "Ali", sub: "Skinplicity — Redesign Client", tag: "Site Redesign", layout: "wide" },
        { quote: "I will spread the word! I like the ease of yours way better!", name: "GAP Outdoors", sub: "After comparing with a previous provider · Trailers & Sheds Client", tag: "", layout: "small" },
        { quote: "Amazing. I just logged in the admin page. I love how this is set up. So easy to navigate.", name: "GAP Outdoors", sub: "On the admin setup · Trailers & Sheds Client", tag: "", layout: "small" },
        { quote: "We love it! It's so professional!", name: "GAP Outdoors", sub: "Trailers & Sheds Client", tag: "", layout: "small" },
    ];
    const testimonialsData = sc.testimonials && sc.testimonials.length ? sc.testimonials : defaultTestimonials;
    const heroRef = useRef(null);
    const [mockupOpen, setMockupOpen] = useState(false);
    const workImgRef = useRef(null);
    const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const bgY = useTransform(heroProgress, [0, 1], ["0%", "22%"]);
    const logoY = useTransform(heroProgress, [0, 1], ["0%", "60%"]);
    const { scrollYProgress: workProgress } = useScroll({ target: workImgRef, offset: ["start end", "end start"] });
    const workImgY = useTransform(workProgress, [0, 1], ["-8%", "8%"]);

    return (
        <div data-testid="home-page">
            <SEO
                title="Camarca Digital Designs | Web Design, Logos & Branding — Cincinnati, Ohio"
                description="Camarca Digital Designs builds affordable, professional websites, logos and branding for businesses of any size and any industry — Cincinnati, Ohio and nationwide. Request a free quote today."
                path="/"
            />

            {/* HERO */}
            <section ref={heroRef} data-testid="hero-section" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
                <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
                    <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-void via-void/60 to-void/20" />
                    <div className="absolute inset-0 bg-grid-animated opacity-70" />
                </motion.div>

                <div className="hero-orb hero-orb-a -left-40 -top-40 h-[480px] w-[480px]" aria-hidden="true" />
                <div className="hero-orb hero-orb-b -bottom-48 -right-32 h-[560px] w-[560px]" aria-hidden="true" />
                <CircuitField className="pointer-events-none absolute inset-0 h-full w-full opacity-75" />

                <motion.div
                    style={{ y: logoY }}
                    aria-hidden="true"
                    className="absolute right-10 top-28 hidden lg:block xl:right-24"
                >
                    <motion.img
                        src="/assets/camarca-logo.png"
                        alt=""
                        initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ duration: 1.4, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="w-56 rounded-full shadow-[0_0_80px_rgba(0,102,255,0.35)] xl:w-72"
                    />
                </motion.div>

                <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="font-mono text-xs tracking-[0.3em] uppercase text-neon"
                    >
                        {"// "}{heroData.tag || "Camarca Digital Designs — Cincinnati, OH & Nationwide"}
                    </motion.p>

                    <h1 className="mt-8 font-display font-black uppercase leading-[0.95] tracking-tight text-[clamp(2.6rem,8.5vw,7rem)]">
                        <KineticLine delay={0.2}>{heroData.line1 || "Websites,"}</KineticLine>
                        <KineticLine delay={0.35}>{heroData.line2 || "Logos &"}</KineticLine>
                        <KineticLine delay={0.5}>
                            <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">{heroData.line3 || "Branding"}</span>
                        </KineticLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.75 }}
                        className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg"
                    >
                        {heroData.subtext || "Clean, modern digital design that makes your business look real — whatever your business is. Any size, any industry, anywhere in the US."}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/contact"
                            data-testid="hero-start-project-button"
                            className="group flex items-center gap-3 bg-neon px-8 py-4 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(0,240,255,0.55)]"
                        >
                            Start a Project
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                        <Link
                            to="/work"
                            data-testid="hero-view-work-button"
                            className="group flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-white transition-[border-color,color] duration-300 hover:border-neon hover:text-neon"
                        >
                            View Work
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="mt-16 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 font-mono text-xs text-zinc-500"
                    >
                        {(heroData.stats && heroData.stats.length ? heroData.stats : [
                            { value: "05", label: "Core services" },
                            { value: "OH", label: "Cincinnati based" },
                            { value: "US", label: "Serving nationwide" },
                        ]).map((s, i) => (
                            <p key={i} data-testid={`hero-stat-${i}`}><span className="text-neon">{s.value}</span> / {s.label}</p>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* MARQUEE */}
            <div data-testid="services-marquee" className="overflow-hidden border-y border-white/10 py-6" aria-hidden="true">
                <Marquee speed={30} gradient={false}>
                    {marqueeItems.map((item) => (
                        <span key={item} className="mx-8 flex items-center gap-16 font-display text-2xl font-bold uppercase tracking-wide md:text-4xl">
                            <span className="text-stroke">{item}</span>
                            <span className="font-mono text-base text-neon md:text-xl">{"//"}</span>
                        </span>
                    ))}
                </Marquee>
            </div>

            {/* REVIEW MARQUEE */}
            <div data-testid="review-marquee" className="border-b border-white/10 bg-surface/60 py-5" aria-label="Client reviews">
                <Marquee speed={20} gradient={false}>
                    {reviews.map((r, i) => (
                        <span key={`${r.name}-${i}`} className="mx-10 flex items-center gap-4">
                            <span className="text-sm italic text-zinc-300 md:text-base">&ldquo;{r.quote}&rdquo;</span>
                            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-neon">
                                — {r.name}, {r.org}
                            </span>
                            <span className="ml-10 font-mono text-zinc-700">{"//"}</span>
                        </span>
                    ))}
                </Marquee>
            </div>

            {/* MANIFESTO */}
            <section data-testid="manifesto-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                <Reveal>
                    <SectionTag>Why Camarca</SectionTag>
                    <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                        Built to make your business look big-league.
                    </h2>
                </Reveal>
                <div className="mt-20">
                    {chaptersData.map((c, i) => (
                        <Reveal key={c.n} delay={i * 0.08}>
                            <div className="grid items-baseline gap-4 border-t border-white/10 py-12 lg:grid-cols-12">
                                <span className="font-mono text-5xl font-medium text-stroke lg:col-span-2">{c.n}</span>
                                <h3 className="font-display text-xl font-bold md:text-2xl lg:col-span-4">{c.title}</h3>
                                <p className="max-w-xl leading-relaxed text-zinc-400 lg:col-span-6">{c.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section data-testid="services-teaser-section" className="mx-auto max-w-7xl px-6 pb-24 md:pb-32 lg:px-10">
                <Reveal className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <SectionTag>What I Do</SectionTag>
                        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-5xl">Services</h2>
                    </div>
                    <Link
                        to="/services"
                        data-testid="services-teaser-link"
                        className="group flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 transition-colors duration-300 hover:text-neon"
                    >
                        All services
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </Reveal>

                <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-12">
                    {services.map((s, i) => {
                        const Icon = iconMap[s.icon];
                        return (
                            <Reveal key={s.id} delay={i * 0.06} className={bentoSpans[i]}>
                                <Link
                                    to="/services"
                                    data-testid={`service-card-${s.id}`}
                                    className={`group relative flex h-full flex-col justify-between border border-white/10 bg-surface p-8 transition-[border-color,box-shadow] duration-500 hover:border-neon/50 hover:shadow-[0_0_30px_rgba(0,240,255,0.08)] ${i === 5 ? "md:flex-row md:items-center md:gap-10" : ""}`}
                                >
                                    <div>
                                        <div className="flex items-start justify-between">
                                            <span className="flex h-11 w-11 items-center justify-center border border-white/10 text-neon">
                                                <Icon className="h-5 w-5" />
                                            </span>
                                            <span className="font-mono text-xs text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                                        </div>
                                        <h3 className="mt-6 font-display text-lg font-bold md:text-xl">{s.title}</h3>
                                        <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-400">{s.tagline}</p>
                                    </div>
                                    <div className="mt-6 flex items-center justify-between">
                                        <span className="font-mono text-xs tracking-widest text-neon">{s.price}</span>
                                        <ArrowUpRight className="h-4 w-4 text-zinc-600 transition-[color,transform] duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-neon" />
                                    </div>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* WORK TEASER */}
            <section data-testid="work-teaser-section" className="border-t border-white/10 bg-surface/40">
                <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                    <Reveal>
                        <SectionTag>Selected Work</SectionTag>
                        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-5xl">
                            Real builds. Real businesses.
                        </h2>
                    </Reveal>

                    <div className="mt-16 grid items-center gap-12 lg:grid-cols-12">
                        <Reveal className="lg:col-span-7">
                            <div ref={workImgRef} className="clip-frame relative overflow-hidden border border-white/10">
                                <motion.img
                                    style={{ y: workImgY }}
                                    src={featuredProject.images[0].src}
                                    alt={featuredProject.images[0].alt}
                                    className="aspect-[4/3] w-full scale-110 object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute left-4 top-4 bg-black/70 px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-neon backdrop-blur-sm">
                                    Latest build
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.15} className="lg:col-span-5">
                            <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">{featuredProject.category}</p>
                            <h3 className="mt-4 font-display text-2xl font-bold md:text-3xl">{featuredProject.name}</h3>
                            <p className="mt-5 leading-relaxed text-zinc-400">{featuredProject.description}</p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {featuredProject.deliverables.map((d) => (
                                    <span key={d} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-zinc-400">
                                        {d}
                                    </span>
                                ))}
                            </div>
                            <Link
                                to="/work"
                                data-testid="work-teaser-link"
                                className="group mt-8 inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-neon"
                            >
                                View the work
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-10 grid grid-cols-3 gap-4">
                        {clientProjects.slice(0, 3).map((p, i) => (
                            <Reveal key={p.name} delay={i * 0.1}>
                                <Link to="/work" className="group block overflow-hidden clip-frame-tl border border-white/10" data-testid={`work-teaser-thumb-${i}`}>
                                    <img
                                        src={p.image}
                                        alt={p.alt}
                                        loading="lazy"
                                        className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section data-testid="process-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                <Reveal>
                    <SectionTag>The Process</SectionTag>
                    <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-5xl">
                        From idea to online.
                    </h2>
                </Reveal>
                <div className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
                    {processStepsData.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1}>
                            <div className="border-t border-white/10 pt-6">
                                <span className="font-mono text-sm text-neon">{s.n}</span>
                                <h3 className="mt-4 font-display text-lg font-bold">{s.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section data-testid="testimonials-section" className="border-t border-white/10 bg-surface/40">
                <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                    <Reveal>
                        <SectionTag>Word of Mouth</SectionTag>
                        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-5xl">
                            Clients say it best.
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid gap-6 lg:grid-cols-12">
                        {testimonialsData.map((t, i) => {
                            const spans = { big: "lg:col-span-7", panel: "lg:col-span-5", wide: "lg:col-span-12", small: "lg:col-span-4" };
                            const layout = spans[t.layout] ? t.layout : "small";
                            return (
                                <Reveal key={i} delay={i * 0.08} className={spans[layout]}>
                                    {layout === "wide" ? (
                                        <figure data-testid={`testimonial-card-${i}`} className="flex h-full flex-col gap-8 border border-white/10 bg-raised p-10 transition-[border-color] duration-500 hover:border-neon/40 md:flex-row md:items-end md:justify-between">
                                            <div>
                                                {t.tag && (
                                                    <span className="border border-neon/40 px-2.5 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-neon">
                                                        {t.tag}
                                                    </span>
                                                )}
                                                <blockquote className="mt-6 font-display text-xl font-bold leading-snug md:text-2xl">
                                                    &ldquo;{t.quote}&rdquo;
                                                </blockquote>
                                            </div>
                                            <figcaption className="shrink-0 md:text-right">
                                                <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-white">{t.name}</p>
                                                <span className="flex gap-0.5" aria-label="5 star review">{[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-neon text-neon" />)}</span>
                                            </div>
                                                <p className="mt-1 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">{t.sub}</p>
                                            </figcaption>
                                        </figure>
                                    ) : layout === "big" ? (
                                        <figure data-testid={`testimonial-card-${i}`} className="flex h-full flex-col justify-between border border-white/10 bg-raised p-10 transition-[border-color] duration-500 hover:border-neon/40">
                                            <div>
                                                <span className="font-mono text-6xl leading-none text-neon" aria-hidden="true">&ldquo;</span>
                                                <blockquote className="mt-4 font-display text-2xl font-bold leading-snug md:text-3xl">{t.quote}</blockquote>
                                            </div>
                                            <figcaption className="mt-10">
                                                <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-white">{t.name}</p>
                                                <span className="flex gap-0.5" aria-label="5 star review">{[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-neon text-neon" />)}</span>
                                            </div>
                                                <p className="mt-1 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">{t.sub}</p>
                                            </figcaption>
                                        </figure>
                                    ) : layout === "panel" ? (
                                        <figure data-testid={`testimonial-card-${i}`} className="flex h-full flex-col justify-between border-l-2 border-deep bg-surface p-10">
                                            <blockquote className="text-lg leading-relaxed text-zinc-200 md:text-xl">
                                                &ldquo;{t.quote}&rdquo;
                                            </blockquote>
                                            <figcaption className="mt-10">
                                                <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-white">{t.name}</p>
                                                <span className="flex gap-0.5" aria-label="5 star review">{[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-neon text-neon" />)}</span>
                                            </div>
                                                <p className="mt-1 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">{t.sub}</p>
                                            </figcaption>
                                        </figure>
                                    ) : (
                                        <figure data-testid={`testimonial-card-${i}`} className="flex h-full flex-col justify-between border border-white/10 bg-surface p-8 transition-[border-color] duration-500 hover:border-neon/40">
                                            <blockquote className="text-base leading-relaxed text-zinc-200">
                                                &ldquo;{t.quote}&rdquo;
                                            </blockquote>
                                            <figcaption className="mt-8">
                                                <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-white">{t.name}</p>
                                                <span className="flex gap-0.5" aria-label="5 star review">{[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-neon text-neon" />)}</span>
                                            </div>
                                                <p className="mt-1 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">{t.sub}</p>
                                            </figcaption>
                                        </figure>
                                    )}
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ABOUT */}
            <section data-testid="about-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
                <div className="grid items-center gap-12 lg:grid-cols-12">
                    <Reveal className="lg:col-span-5">
                        <div className="clip-frame relative overflow-hidden border border-white/10">
                            <img
                                src="/assets/owner-portrait.webp"
                                alt="Founder and designer of Camarca Digital Designs, Cincinnati Ohio web designer"
                                data-testid="about-portrait"
                                className="aspect-[4/5] w-full object-cover object-top"
                                loading="lazy"
                            />
                            <div className="absolute bottom-4 left-4 bg-black/70 px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-neon backdrop-blur-sm">
                                {aboutData.badge || "Nick · Founder & Designer"}
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-7">
                        <SectionTag>About</SectionTag>
                        <h2 className="mt-6 font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                            {aboutData.heading || "One studio. One designer. Zero runaround."}
                        </h2>
                        <div className="mt-8 max-w-2xl space-y-5 leading-relaxed text-zinc-400">
                            {(aboutData.paragraphs && aboutData.paragraphs.length ? aboutData.paragraphs : [
                                "I'm Nick — the designer and builder behind Camarca Digital Designs, a one-person studio based in Cincinnati, Ohio.",
                                "I work a full-time job and started building websites on the side as a hobby. What turned it into something more was realizing how many business owners are great at what they do but nearly invisible online — and how much a real, professional presence changes that.",
                                "So every Camarca project gets personal attention: you talk directly with the person designing, building, and updating your site. No account managers, no handoffs, no getting lost in a queue — and no agency price tag.",
                            ]).map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                            <p className="font-mono text-sm text-neon">{aboutData.signoff || "— Nick"}</p>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {(aboutData.chips && aboutData.chips.length ? aboutData.chips : ["Cincinnati, Ohio", "One-person studio", "Direct communication", "Any size business"]).map((t) => (
                                <span key={t} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-zinc-400">
                                    {t}
                                </span>
                            ))}
                        </div>
                        <Link
                            to="/contact"
                            data-testid="about-cta"
                            className="group mt-10 inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-neon"
                        >
                            Work with me
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* MOCKUP OFFER */}
            {(sc.mockupOffer?.enabled ?? true) && (
                <section data-testid="mockup-offer-section" className="relative overflow-hidden border-y border-white/10 bg-surface/40">
                    <div className="bg-scanlines absolute inset-0" aria-hidden="true" />
                    <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28 lg:px-10">
                        <div className="grid items-center gap-12 lg:grid-cols-12">
                            <Reveal className="lg:col-span-7">
                                <SectionTag>Free Preview</SectionTag>
                                <h2 className="mt-6 font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                                    {sc.mockupOffer?.heading || "Not ready to commit? Get a free homepage preview."}
                                </h2>
                                <p className="mt-6 max-w-xl leading-relaxed text-zinc-400 md:text-lg">
                                    {sc.mockupOffer?.text || "Send me your business name and what you do — I'll design a homepage mockup so you can see your new look before you pay a dime. Keep it only if you love it."}
                                </p>
                                <button
                                    onClick={() => setMockupOpen(true)}
                                    data-testid="mockup-offer-cta"
                                    className="group mt-10 inline-flex items-center gap-3 bg-neon px-8 py-4 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(0,240,255,0.55)]"
                                >
                                    {sc.mockupOffer?.ctaLabel || "Claim My Free Preview"}
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                </button>
                                <div className="mt-12 grid grid-cols-3 gap-4">
                                    {[
                                        { n: "01", t: "Send your details" },
                                        { n: "02", t: "I design a preview" },
                                        { n: "03", t: "You decide — free means free" },
                                    ].map((s) => (
                                        <div key={s.n}>
                                            <span className="font-mono text-sm text-neon">{s.n}</span>
                                            <p className="mt-2 text-sm font-medium leading-snug text-zinc-300">{s.t}</p>
                                        </div>
                                    ))}
                                </div>
                            </Reveal>
                            <Reveal delay={0.15} className="lg:col-span-5">
                                <div className="relative">
                                    {[
                                        { src: "/assets/salon-before-after.webp", alt: "Example salon homepage mockup by Camarca Digital Designs", rot: "-rotate-2" },
                                        { src: "/assets/auto-body-before-after.webp", alt: "Example auto body homepage mockup by Camarca Digital Designs", rot: "rotate-1" },
                                        { src: "/assets/hvac-before-after.webp", alt: "Example HVAC homepage mockup by Camarca Digital Designs", rot: "-rotate-1" },
                                    ].map((p, i) => (
                                        <div
                                            key={p.src}
                                            data-testid={`mockup-preview-${i}`}
                                            className={`group mb-4 overflow-hidden border border-white/10 bg-void ${p.rot} transition-transform duration-500 last:mb-0 hover:rotate-0`}
                                        >
                                            <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-2">
                                                <span className="h-2 w-2 rounded-full bg-zinc-700" />
                                                <span className="h-2 w-2 rounded-full bg-zinc-700" />
                                                <span className="h-2 w-2 rounded-full bg-neon/60" />
                                                <span className="ml-3 font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-600">Sample mockup</span>
                                            </div>
                                            <img
                                                src={p.src}
                                                alt={p.alt}
                                                loading="lazy"
                                                className="aspect-[16/9] w-full object-cover object-top blur-[2px] transition-[filter] duration-500 group-hover:blur-0"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section data-testid="cta-section" className="relative overflow-hidden border-t border-white/10">
                <div className="bg-scanlines absolute inset-0" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl px-6 py-28 md:py-40 lg:px-10">
                    <Reveal>
                        <SectionTag>Ready When You Are</SectionTag>
                        <h2 className="mt-8 font-display font-black uppercase leading-[0.95] tracking-tight text-[clamp(2.4rem,7vw,5.5rem)]">
                            Let's build
                            <br />
                            <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">something real.</span>
                        </h2>
                        <div className="mt-12 flex flex-wrap items-center gap-6">
                            <Link
                                to="/contact"
                                data-testid="cta-start-project-button"
                                className="group flex items-center gap-3 bg-neon px-8 py-4 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(0,240,255,0.55)]"
                            >
                                Start a Project
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </Link>
                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                data-testid="cta-email-link"
                                className="font-mono text-xs tracking-[0.15em] text-zinc-400 underline decoration-zinc-700 underline-offset-8 transition-colors duration-300 hover:text-neon hover:decoration-neon"
                            >
                                {CONTACT_EMAIL}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            <MockupModal open={mockupOpen} onClose={() => setMockupOpen(false)} />
        </div>
    );
}
